import { createClient } from '@/lib/supabase/client';

// ─── Type helpers ────────────────────────────────────────────────────────────

interface PostgresError {
  code?: string;
  message?: string;
}

function isSchemaError(error: unknown): boolean {
  if (!error) return false;
  const err = error as PostgresError;
  if (err.code && typeof err.code === 'string') {
    const cls = err.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (err.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return patterns.some((p) => p.test(err.message!));
  }
  return false;
}

// ─── Organization context ────────────────────────────────────────────────────

// REMOVED: DEFAULT_ORG_SLUG hardcoded fallback ('truelight-web-agency')
// resolveOrgId() now returns null if no organization is found for the user.
// Callers must handle null and return a proper error — never fall back to another org.

export async function resolveOrgId(organizationId?: string | null): Promise<string | null> {
  if (organizationId) return organizationId;
  const supabase = createClient();
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('[resolveOrgId] No authenticated user — returning null');
      return null;
    }
    const { data: membership } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('joined_at', { ascending: true })
      .limit(1)
      .single();
    if (membership?.organization_id) return membership.organization_id;
    console.warn('[resolveOrgId] No active org membership for user', user.id);
    return null;
  } catch (err) {
    console.error('[resolveOrgId] Error resolving organization:', err instanceof Error ? err.message : err);
    return null;
  }
}

export const organizationService = {
  async getAll() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },

  async getMembers(organizationId: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select('*, user_profiles(id, full_name, email, avatar_url)')
        .eq('organization_id', organizationId)
        .eq('is_active', true);
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },

  async update(id: string, updates: Record<string, unknown>) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getUserRole(organizationId: string): Promise<string | null> {
    const supabase = createClient();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from('organization_members').select('role').eq('organization_id', organizationId).eq('user_id', user.id).eq('is_active', true)
        .single();
      return data?.role ?? null;
    } catch {
      return null;
    }
  },
};

// ─── Leads ───────────────────────────────────────────────────────────────────

export const leadsService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async create(lead: Record<string, any>, organizationId?: string) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    const { data, error } = await supabase
      .from('leads')
      .insert({ ...lead, organization_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Record<string, any>) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('leads')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const supabase = createClient();
    const { error } = await supabase.from('leads').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Clients ─────────────────────────────────────────────────────────────────

export interface Client {
  id: string;
  organization_id?: string;
  business_name: string;
  owner_name: string;
  phone?: string;
  email?: string;
  website?: string;
  domain?: string;
  logo_url?: string;
  industry?: string;
  address?: string;
  package_id?: string;
  payment_status?: 'pending' | 'paid' | 'overdue' | 'refunded';
  hosting_status?: 'active' | 'suspended' | 'cancelled' | 'pending';
  maintenance_active?: boolean;
  project_status?: string;
  monthly_payment?: number;
  launch_date?: string;
  renewal_date?: string;
  google_business_status?: string;
  seo_status?: string;
  ssl_status?: string;
  booking_system_status?: string;
  notes?: string;
  meeting_notes?: string;
  tags?: string[];
  assigned_to?: string;
  created_by?: string;
  lead_id?: string;
  created_at?: string;
  updated_at?: string;
  packages?: { name: string; slug: string } | null;
}

export const clientsService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('clients')
        .select('*, packages(name, slug)')
        .order('created_at', { ascending: false });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []) as Client[];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async getById(id: string) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('clients')
      .select('*, packages(name, slug, description, features)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as Client;
  },

  async create(client: Record<string, any>, organizationId?: string) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    const { data, error } = await supabase
      .from('clients')
      .insert({ ...client, organization_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data as Client;
  },

  async update(id: string, updates: Record<string, unknown>) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('clients')
      .update(updates)
      .eq('id', id)
      .select('*, packages(name, slug)')
      .single();
    if (error) throw error;
    return data as Client;
  },

  async delete(id: string) {
    const supabase = createClient();
    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) throw error;
  },

  async search(query: string, organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let dbQuery = supabase
        .from('clients')
        .select('*, packages(name, slug)')
        .or(
          `business_name.ilike.%${query}%,owner_name.ilike.%${query}%,email.ilike.%${query}%,industry.ilike.%${query}%`
        )
        .order('created_at', { ascending: false });
      if (orgId) {
        dbQuery = dbQuery.eq('organization_id', orgId);
      }
      const { data, error } = await dbQuery;
      if (error) return [];
      return (data ?? []) as Client[];
    } catch {
      return [];
    }
  },

  async getFiles(clientId: string, projectId?: string, organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('client_files')
        .select('*, user_profiles(full_name)')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      if (projectId) {
        query = query.eq('project_id', projectId);
      }
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },

  async addFile(file: Record<string, any>, organizationId?: string) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    const { data, error } = await supabase
      .from('client_files')
      .insert({ ...file, organization_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteFile(fileId: string) {
    const supabase = createClient();
    const { error } = await supabase.from('client_files').delete().eq('id', fileId);
    if (error) throw error;
  },

  async getActivity(clientId: string, limit = 50, organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('client_activity')
        .select('*, user_profiles(full_name, avatar_url)')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },

  async logActivity(
    clientId: string,
    action: string,
    description: string,
    performedBy?: string,
    organizationId?: string,
    metadata?: Record<string, unknown>
  ) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    try {
      await supabase.from('client_activity').insert({
        client_id: clientId,
        organization_id: orgId,
        action,
        description,
        performed_by: performedBy,
        metadata: metadata ?? {},
      });
    } catch {
      // Non-critical — don't throw
    }
  },

  async getProjects(clientId: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('projects').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },

  async getInvoices(clientId: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('invoices').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },

  async getContracts(clientId: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('contracts').select('*').eq('client_id', clientId).order('created_at', { ascending: false });
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },
};

// ─── Projects ────────────────────────────────────────────────────────────────

export const projectsService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('projects')
        .select('*, clients(business_name), packages(name)')
        .order('created_at', { ascending: false });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async update(id: string, updates: Record<string, any>) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async create(project: Record<string, any>, organizationId?: string) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    const { data, error } = await supabase
      .from('projects')
      .insert({ ...project, organization_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getActive(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('projects')
        .select('*, clients(business_name)')
        .not('status', 'in', '("live","archived")')
        .order('deadline', { ascending: true });
      if (orgId) query = query.eq('organization_id', orgId);
      const { data, error } = await query;
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },
};

// ─── Tasks ───────────────────────────────────────────────────────────────────

export const tasksService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('tasks')
        .select('*, projects(name, clients(business_name))')
        .order('due_date', { ascending: true });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async getDueToday(organizationId?: string) {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('tasks')
        .select('*, projects(name, clients(business_name))')
        .eq('due_date', today)
        .neq('status', 'done');
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async getUpcoming(organizationId?: string, days = 7) {
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];
    const future = new Date(Date.now() + days * 86400000).toISOString().split('T')[0];
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('tasks')
        .select('*, projects(name, clients(business_name))')
        .gte('due_date', today)
        .lte('due_date', future)
        .neq('status', 'done')
        .order('due_date', { ascending: true });
      if (orgId) query = query.eq('organization_id', orgId);
      const { data, error } = await query;
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },
};

// ─── Invoices ────────────────────────────────────────────────────────────────

export const invoicesService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('invoices')
        .select('*, clients(business_name)')
        .order('created_at', { ascending: false });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async getSummary(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase.from('invoices').select('amount, status');
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return { pending: 0, unpaid: 0, pendingCount: 0, unpaidCount: 0 };
      }
      const rows = data ?? [];
      const pending = rows.filter((r) => r.status === 'sent');
      const unpaid = rows.filter((r) => r.status === 'overdue');
      return {
        pending: pending.reduce((s, r) => s + Number(r.amount), 0),
        unpaid: unpaid.reduce((s, r) => s + Number(r.amount), 0),
        pendingCount: pending.length,
        unpaidCount: unpaid.length,
      };
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return { pending: 0, unpaid: 0, pendingCount: 0, unpaidCount: 0 };
    }
  },

  async create(invoice: Record<string, any>, organizationId?: string) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    const { data, error } = await supabase
      .from('invoices')
      .insert({ ...invoice, organization_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getOpenInvoices(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('invoices')
        .select('*, clients(business_name)')
        .in('status', ['sent', 'overdue'])
        .order('due_date', { ascending: true });
      if (orgId) query = query.eq('organization_id', orgId);
      const { data, error } = await query;
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },
};

// ─── Support Tickets ─────────────────────────────────────────────────────────

export const ticketsService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('support_tickets')
        .select('*, clients(business_name)')
        .order('created_at', { ascending: false });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async getOpenCount(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('support_tickets')
        .select('*', { count: 'exact', head: true })
        .in('status', ['open', 'in_progress', 'waiting_client']);
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { count, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return 0;
      }
      return count ?? 0;
    } catch {
      return 0;
    }
  },

  async create(ticket: Record<string, any>, organizationId?: string) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    const { data, error } = await supabase
      .from('support_tickets')
      .insert({ ...ticket, organization_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ─── Contracts ───────────────────────────────────────────────────────────────

export const contractsService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('contracts')
        .select('*, clients(business_name)')
        .order('created_at', { ascending: false });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async create(contract: Record<string, any>, organizationId?: string) {
    const supabase = createClient();
    const orgId = organizationId ?? await resolveOrgId();
    const { data, error } = await supabase
      .from('contracts')
      .insert({ ...contract, organization_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ─── Packages ────────────────────────────────────────────────────────────────

export const packagesService = {
  async getAll(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('packages')
        .select('*')
        .order('sort_order', { ascending: true });
      if (orgId) {
        query = query.eq('organization_id', orgId);
      }
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data ?? [];
    } catch (err: any) {
      if (isSchemaError(err)) throw err;
      return [];
    }
  },

  async update(id: string, updates: Record<string, any>) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('packages')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ─── Notifications ───────────────────────────────────────────────────────────

export interface Notification {
  id: string;
  organization_id?: string;
  user_id: string;
  title: string;
  message?: string;
  type: 'invoice' | 'contract' | 'file' | 'project' | 'task' | 'system' | 'info';
  entity_type?: string;
  entity_id?: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

export const notificationsService = {
  async getAll(userId: string, organizationId?: string): Promise<Notification[]> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (organizationId) query = query.eq('organization_id', organizationId);
      const { data, error } = await query;
      if (error) {
        if (isSchemaError(error)) return [];
        return [];
      }
      return (data ?? []) as Notification[];
    } catch {
      return [];
    }
  },

  async getUnreadCount(userId: string): Promise<number> {
    const supabase = createClient();
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);
      if (error) return 0;
      return count ?? 0;
    } catch {
      return 0;
    }
  },

  async markAsRead(notificationId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
    } catch {
      // Non-critical
    }
  },

  async markAllAsRead(userId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);
    } catch {
      // Non-critical
    }
  },

  async create(notification: Omit<Notification, 'id' | 'created_at'>): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.from('notifications').insert(notification);
    } catch {
      // Non-critical
    }
  },

  async delete(notificationId: string): Promise<void> {
    const supabase = createClient();
    try {
      await supabase.from('notifications').delete().eq('id', notificationId);
    } catch {
      // Non-critical
    }
  },
};

// ─── Client Activity (org-wide) ───────────────────────────────────────────────

export const activityService = {
  async getRecent(organizationId?: string, limit = 20) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();
      let query = supabase
        .from('client_activity')
        .select('*, clients(business_name), user_profiles(full_name, avatar_url)')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (orgId) query = query.eq('organization_id', orgId);
      const { data, error } = await query;
      if (error) return [];
      return data ?? [];
    } catch {
      return [];
    }
  },
};

// ─── Dashboard Metrics ───────────────────────────────────────────────────────
type DashboardLead = {
  created_at: string | null;
  status: string | null;
  pipeline_stage: string | null;
  lead_value: number | null;
  estimated_value: number | null;
};

type DashboardClient = {
  created_at: string | null;
  hosting_status: string | null;
  maintenance_active: boolean | null;
};

type DashboardInvoice = {
  amount: number | string | null;
  status: string | null;
  created_at: string | null;
  paid_at: string | null;
  due_date: string | null;
};
export const dashboardService = {
  async getMetrics(organizationId?: string) {
    const supabase = createClient();
    try {
      const orgId = organizationId ?? await resolveOrgId();

      const buildQuery = <T extends 'leads' | 'clients' | 'invoices'>(
  table: T,
  select: string
) => {
  let q = supabase.from(table).select(select);

  if (orgId) {
    q = q.eq('organization_id', orgId);
  }

  return q;
};

      const [leadsRes, clientsRes, projectsRes, invoicesRes, ticketsRes, tasksRes, activityRes, onboardingRes] =
        await Promise.all([
          buildQuery('leads', 'status, pipeline_stage, lead_value, estimated_value, created_at'),
          buildQuery('clients', 'hosting_status, maintenance_active, created_at'),
          (() => {
            let q = supabase
              .from('projects')
              .select('status, deadline, due_date, progress')
              .not('status', 'in', '("live","archived")');
            if (orgId) q = q.eq('organization_id', orgId);
            return q;
          })(),
          buildQuery('invoices', 'amount, status, created_at, paid_at, due_date'),
          (() => {
            let q = supabase
              .from('support_tickets')
              .select('status')
              .in('status', ['open', 'in_progress', 'waiting_client']);
            if (orgId) q = q.eq('organization_id', orgId);
            return q;
          })(),
          (() => {
            const today = new Date().toISOString().split('T')[0];
            let q = supabase
              .from('tasks')
              .select('due_date, status')
              .eq('due_date', today)
              .neq('status', 'done');
            if (orgId) q = q.eq('organization_id', orgId);
            return q;
          })(),
          (() => {
            let q = supabase
              .from('client_activity')
              .select('action, created_at, clients(business_name)')
              .order('created_at', { ascending: false })
              .limit(5);
            if (orgId) q = q.eq('organization_id', orgId);
            return q;
          })(),
          (() => {
            let q = supabase
              .from('onboarding_checklists')
              .select('status', { count: 'exact', head: false })
              .eq('status', 'in_progress');
            if (orgId) q = q.eq('organization_id', orgId);
            return q;
          })(),
        ]);

      const leads = (leadsRes.data ?? []) as unknown as DashboardLead[];
const clients = (clientsRes.data ?? []) as unknown as DashboardClient[];
const projects = (projectsRes.data ?? []) as unknown as {
  status: string;
  deadline?: string | null;
  due_date?: string | null;
  progress?: number | null;
}[];
const invoices = (invoicesRes.data ?? []) as unknown as DashboardInvoice[];
const tickets = ticketsRes.data ?? [];
const tasks = tasksRes.data ?? [];
const recentActivity = activityRes.data ?? [];
const onboardingData = onboardingRes.data ?? [];

      const today = new Date().toISOString().split('T')[0];
      const newLeadsToday = leads.filter(
        (l) => l.created_at?.split('T')[0] === today
      ).length;

      const hostingClients = clients.filter((c) => c.hosting_status === 'active').length;
      const maintenanceClients = clients.filter((c) => c.maintenance_active).length;

      const dueThisWeek = projects.filter((p) => {
        const deadline = p.due_date || p.deadline;
        if (!deadline) return false;
        const d = new Date(deadline);
        const now = new Date();
        const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        return d >= now && d <= weekFromNow;
      }).length;

      const pendingInvoices = invoices.filter((i) => i.status === 'sent');
      const unpaidInvoices = invoices.filter((i) => i.status === 'overdue');
      const paidThisMonth = invoices
        .filter((i) => {
          if (i.status !== 'paid') return false;
          const d = new Date(i.paid_at ?? i.created_at ?? new Date().toISOString());
          const now = new Date();
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        })
        .reduce((s, i) => s + Number(i.amount), 0);

      // Revenue by month (last 6 months)
      const revenueByMonth: Record<string, number> = {};
      invoices
        .filter((i) => i.status === 'paid')
        .forEach((i) => {
          const d = new Date(i.paid_at ?? i.created_at ?? new Date().toISOString());
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          revenueByMonth[key] = (revenueByMonth[key] ?? 0) + Number(i.amount);
        });

      // Lead conversion rate
      const wonLeads = leads.filter((l) => l.status === 'won' || l.pipeline_stage === 'won').length;
      const leadConversionRate = leads.length > 0 ? Math.round((wonLeads / leads.length) * 100) : 0;

      // Pipeline value (active leads not won/lost)
      const activeLeads = leads.filter(l => !['won', 'lost'].includes(l.pipeline_stage ?? l.status ?? ''));
      const pipelineValue = activeLeads.reduce((s, l) => s + Number(l.lead_value ?? l.estimated_value ?? 0), 0);

      // Client growth (new clients this month)
      const newClientsThisMonth = clients.filter(c => {
        const d = new Date(c.created_at ?? new Date().toISOString());
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;

      const totalClients = clients.length;
      const totalLeads = leads.length;

      return {
        newLeadsToday,
        totalLeads,
        totalClients,
        newClientsThisMonth,
        hostingClients,
        maintenanceClients,
        activeProjects: projects.length,
        projectsDueThisWeek: dueThisWeek,
        pendingInvoicesCount: pendingInvoices.length,
        pendingInvoicesAmount: pendingInvoices.reduce((s, i) => s + Number(i.amount), 0),
        unpaidInvoicesCount: unpaidInvoices.length,
        unpaidInvoicesAmount: unpaidInvoices.reduce((s, i) => s + Number(i.amount), 0),
        supportTickets: tickets.length,
        tasksDueToday: tasks.length,
        monthlyRevenue: paidThisMonth,
        // MRR derived from paid invoices recurring amounts; hardcoded rates are a known debt
        mrr: hostingClients * 99 + maintenanceClients * 150,
        revenueByMonth,
        leadConversionRate,
        wonLeads,
        recentActivity,
        avgProjectProgress: projects.length > 0
          ? Math.round(projects.reduce((s, p) => s + (p.progress ?? 0), 0) / projects.length)
          : 0,
        pipelineValue,
        activeLeadsCount: activeLeads.length,
        activeOnboardings: onboardingData.length,
      };
    } catch {
      return null;
    }
  },
};
